import { useDomainDataMethods } from '@b2ag/micro-front-tools/useDomainDataMethod'
import type { DomainDataMethodReturn } from '@b2ag/micro-front-tools/useDomainDataMethod'
import { getAppContext, getConfig } from '@b2ag/micro-front-tools'
import { featuresForCurrentBrand } from '@b2ag/app-brands/src/features'
import UserService from './services/user.service'
import { computed, ref } from 'vue'
import type { UserData, TargetCustomer, Profile, UserDataComposable } from './types'
import { useAuthService } from '@b2ag/auth'

const MEMBERSHIP_LOCAL_STORAGE_KEY = 'targetCustomer'

// --- Lazy singleton service ---
let userService: InstanceType<typeof UserService> | null = null

function getUserService(): InstanceType<typeof UserService> {
  if (!userService) {
    userService = new UserService(getConfig('USER_API_HOST'))
  }
  return userService
}

// --- Module-level singleton state ---
let initialized = false
let domainData: DomainDataMethodReturn<UserData>
let pendingExecute: Promise<UserData | undefined> | null = null
const isAuthenticatedRef = ref(false)
const currentProfileIdOverride = ref<string | null>(null)
const targetCustomerRef = ref<TargetCustomer | null>(null)
const cguAcceptedAtOverride = ref<string | null>(null)
const pendingProfileRef = ref<any>(null)

function init(): void {
  if (initialized) return

  const handler = () => getUserService().get()

  domainData = useDomainDataMethods<UserData>(handler, true, 'user-data')

  try {
    const stored = localStorage.getItem(MEMBERSHIP_LOCAL_STORAGE_KEY)
    targetCustomerRef.value = stored ? JSON.parse(stored) : null
  } catch {
    /* noop */
  }

  initialized = true

  // Auto-load: fire-and-forget (tracked for dedup)
  pendingExecute = useAuthService()
    .isAuthenticated()
    .then((isAuthenticated) => {
      isAuthenticatedRef.value = isAuthenticated
      if (isAuthenticated) return domainData.execute()
    })
    .finally(() => {
      pendingExecute = null
    })
}

/**
 * @deprecated For unit test only
 */
export function resetUserDataState(): void {
  initialized = false
  userService = null
  pendingExecute = null
  isAuthenticatedRef.value = false
  currentProfileIdOverride.value = null
  targetCustomerRef.value = null
  cguAcceptedAtOverride.value = null
  pendingProfileRef.value = null
}

// --- Public composable ---
export function useUserData(): UserDataComposable {
  init()

  const { isFrontAgri } = getAppContext()

  // --- Domain-specific computed (derived from domainData.state) ---
  const user = computed<UserData | undefined>(() => domainData.state.value.data as UserData | undefined)
  const isLoading = computed(() => domainData.state.value.status === 'loading')
  const isUserLoaded = computed(() => domainData.state.value.status === 'success')
  const profiles = computed<Profile[]>(() => (user.value?.profiles as Profile[]) ?? [])

  // --- Profile (agri) ---
  const currentProfileId = computed<string | null>(
    () => currentProfileIdOverride.value ?? (user.value?.currentProfileId as string | null) ?? null,
  )

  const currentProfile = computed<Profile | undefined>(() => {
    const id = currentProfileId.value
    if (!id) return profiles.value[0]
    return profiles.value.find((p) => `${p.id}` === `${id}`) ?? profiles.value[0]
  })

  const currentMembershipNumber = computed(() => currentProfile.value?.membershipNumber)
  const currentCompanyName = computed(() => currentProfile.value?.companyName)
  const currentTechnicalSales = computed(() => currentProfile.value?.technicalSales)

  const hasConfirmedProfile = computed(
    () => profiles.value.length > 0 && profiles.value.some((p) => p.confirmedAt != null),
  )

  const hasCurrentProfile = computed(() => Boolean(currentProfileId.value))

  // --- Cooperative ---
  /**
   * @see Renamed from `currentCooperativeId` for better clarity
   */
  const currentDistributorId = computed<number | undefined>(() => {
    if (isFrontAgri) {
      if (typeof currentProfile.value?.cooperative?.id === 'string')
        return parseInt(currentProfile.value?.cooperative?.id)
      else return currentProfile.value?.cooperative?.id
    }

    if (typeof user.value?.technical_sale_information?.cooperative_id === 'string')
      return parseInt(user.value?.technical_sale_information?.cooperative_id)
    else return user.value?.technical_sale_information?.cooperative_id
  })

  /**
   * @see Renamed from `currentCooperative` for better clarity
   */
  const currentDistributor = computed(() => {
    if (isFrontAgri) {
      return currentProfile.value?.cooperative
    }
    return user.value?.cooperatives?.find(
      (c) => parseInt(String(c.id), 10) === user.value?.technical_sale_information?.cooperative_id,
    )
  })

  /**
   * @see Renamed from `currentCooperativeName` for better clarity
   */
  const currentDistributorName = computed(() => currentDistributor.value?.name)

  // --- Permissions / Certificates ---
  const isCropProtectionAllowed = computed(() => {
    if (isFrontAgri) {
      if (!featuresForCurrentBrand.certificates.phyto) return true
      const profile = currentProfile.value
      return (
        !!profile?.validCertiphyto && (!profile?.certiphytoEndDate || new Date(profile.certiphytoEndDate) > new Date())
      )
    }
    // Tech: from targetCustomer
    return !!targetCustomerRef.value?.valid_certiphyto
  })

  const isExplosivesPrecursorAllowed = computed(() => {
    const isFeatureEnabled = getConfig('EXPLOSIVES_PRECURSOR_FEATURE') === 'yes'
    if (!isFeatureEnabled) return true

    if (isFrontAgri) {
      const profile = currentProfile.value
      const isValid = !!profile?.validExplosivesPrecursor
      const isExpired = profile?.explosivesPrecursorEndDate && new Date(profile.explosivesPrecursorEndDate) < new Date()
      return isValid && !isExpired
    }
    return !!targetCustomerRef.value?.valid_explosives_precursor
  })

  // --- CGU ---
  const isCGUAccepted = computed(() => {
    if (cguAcceptedAtOverride.value) return true
    return isFrontAgri ? !!user.value?.farmer_cgu_accepted_at : !!user.value?.tech_cgu_accepted_at
  })

  // --- Tech-specific ---
  const targetCustomer = computed(() => targetCustomerRef.value)

  const targetCustomerMembership = computed(
    () => targetCustomerRef.value?.membership_number ?? targetCustomerRef.value?.membershipNumber,
  )

  const technicalSaleInformation = computed(() => user.value?.technical_sale_information)

  const analyticsUserPayload = computed(() => ({
    loginStatus: isUserLoaded.value ? 1 : 0,
    cooperativeId: currentDistributorId.value ? `${currentDistributorId.value}` : undefined,
    cooperativeName: currentDistributorName.value,
    membershipNumber: targetCustomerMembership.value,
    id: user.value?.uid,
    type: 'sales',
    technical_sales_number: user.value?.technical_sale_information?.uuid,
    technical_sales_id: user.value?.technical_sale_information?.id,
  }))

  // --- Consent ---
  const consentToken = computed(() => user.value?.consent_token)

  // --- Augmented reset: standard reset + clear domain refs ---
  const reset = (): void => {
    domainData.reset()
    isAuthenticatedRef.value = false
    currentProfileIdOverride.value = null
    targetCustomerRef.value = null
    cguAcceptedAtOverride.value = null
    pendingProfileRef.value = null
  }

  // --- Domain-specific actions ---
  const saveUser = async (data: any): Promise<any> => {
    const result = await getUserService().put(data)
    if (result) {
      domainData.state.value = {
        data: result,
        status: 'success',
        error: undefined,
      } as any
    }
    return result
  }

  const setCurrentProfileId = (profileId: string): void => {
    currentProfileIdOverride.value = profileId
  }

  const setTargetCustomer = (customer: TargetCustomer): void => {
    targetCustomerRef.value = { ...customer }
    localStorage.setItem(MEMBERSHIP_LOCAL_STORAGE_KEY, JSON.stringify(customer))
  }

  const setCGUAcceptedAt = (acceptedAt: string): void => {
    cguAcceptedAtOverride.value = acceptedAt
  }

  const setPendingProfile = (profile: any): void => {
    pendingProfileRef.value = profile
  }

  const setAuthenticated = (value: boolean): void => {
    isAuthenticatedRef.value = value
  }

  // Dedup-aware execute: returns pending promise if auto-load is still in flight
  const execute = async (opts?: any): Promise<UserData | undefined | null> => {
    if (pendingExecute) return pendingExecute
    pendingExecute = domainData.execute(opts).finally(() => {
      pendingExecute = null
    })
    return pendingExecute
  }

  return {
    // Standard useDomainDataMethods API
    state: domainData.state,
    execute,
    refresh: domainData.refresh,
    reset,

    // Domain-specific computed
    user,
    isUserLoaded,
    isLoading,

    profiles,
    currentProfile,
    currentProfileId,
    currentMembershipNumber,
    currentCompanyName,
    currentTechnicalSales,
    hasConfirmedProfile,
    hasCurrentProfile,
    pendingProfile: computed(() => pendingProfileRef.value),

    currentDistributor,
    currentDistributorId,
    currentDistributorName,

    isCropProtectionAllowed,
    isExplosivesPrecursorAllowed,

    isCGUAccepted,

    targetCustomer,
    targetCustomerMembership,
    technicalSaleInformation,
    analyticsUserPayload,

    consentToken,

    // Auth state
    isAuthenticated: computed(() => isAuthenticatedRef.value),
    setAuthenticated,

    // Domain-specific actions
    saveUser,
    setCurrentProfileId,
    setTargetCustomer,
    setCGUAcceptedAt,
    setPendingProfile,
  }
}
